import { useRecoilValue } from 'recoil';

import { dailyForecastState } from '@store';

import { formatPrecipChance, formatPrecipType, formatTemperature } from '../helpers/DataFormatting';
import { TimeUtils } from '../helpers/TimeUtils';
import { DailyForecastData } from '../store/types';
import { DayOfWeek } from '../store/types/commonTypes';

const getFilteredData = (data?: DailyForecastData) => {
  if (!data?.daypart?.length) {
    return [];
  }

  const { daypartName, iconCodeExtend, precipChance, precipType, temperature } = data.daypart[0];
  const { validTimeUtc, dayOfWeek } = data;

  return Array.from({ length: 7 }, (_, index) => {
    const modifiedIndex = index * 2; // So we can grab only day or night indexes at one time

    const dayOfWeekDate = TimeUtils.dateFromEpoch(validTimeUtc[index]);

    return {
      key: validTimeUtc[index],
      dayOfWeekToday: dayOfWeek[0],
      daypartName: daypartName[modifiedIndex],
      iconCodeExtend: iconCodeExtend[modifiedIndex],
      temperatureHigh: temperature[modifiedIndex],
      temperatureLow: temperature[modifiedIndex + 1],
      precipChance: precipChance[modifiedIndex],
      precipType: precipType[modifiedIndex],
      dayAbbreviation: validTimeUtc[index] ? TimeUtils.dayOfWeekAbbr(dayOfWeekDate) : null,
    };
  });
};

type FilteredData = ReturnType<typeof getFilteredData>;

const getFormattedPages = (dayPages: FilteredData) => {
  const finalDataObject: Record<string, NewPage[]> = {};

  for (const [index, page] of dayPages.entries()) {
    const newPage = [
      {
        ...page,
        temperatureHigh: formatTemperature(page.temperatureHigh),
        temperatureLow: formatTemperature(page.temperatureLow),
        precipChance: formatPrecipChance(page.precipChance),
        precipType: formatPrecipType(page.precipType),
      },
    ];

    finalDataObject[`page${index + 1}`] = newPage;
  }

  return finalDataObject;
};

export const useTwoDayForecast = () => {
  const { data } = useRecoilValue(dailyForecastState);

  if (!data || Object.keys(data).length === 0) {
    return null;
  }
  const filteredData = getFilteredData(data);

  // Slice the data to get the next two days, excluding today and tomorrow
  const fromDays = 2;
  const toDays = 4;
  const trimmedData = filteredData.slice(fromDays, toDays);

  return getFormattedPages(trimmedData);
};

type NewPage = {
  key: number;
  dayOfWeekToday: DayOfWeek;
  daypartName: string;
  dayAbbreviation: string | null;
  iconCodeExtend: number;
  temperatureHigh: string;
  temperatureLow: string;
  precipChance: string;
  precipType: string;
};
